import React, {
    ComponentPropsWithRef,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react'
import { twMerge } from 'tailwind-merge'
import { Button } from '@mui/material'
import {
    Assignation,
    CommonValueType,
} from '../../context/types'
import { useTranslation } from '../../i18n/useTranslation'
import { DraggableAssignationList } from '../../components/dnd/DraggableAssignationList'
import { ScaleHint } from '../../components/ScaleHint'
import { DFType } from '../../components/MTypo/DynamicFont'
import { MAX_BUCKET_RANKING } from '../../components/progress/useProgress'
import _ from 'lodash'
import { SinglePicker, partialBucketFromRounds } from './bucket'
import { info, logException } from '../../services/sentryLogger'
import { SessionData } from '../../context/session/types'
import { MainTitle } from '../../components/MainTitle'

interface Props extends ComponentPropsWithRef<'div'> {
    commons: CommonValueType
}

export const RankForBucket = (p: Props) => {
    const { className, commons, ...rest } = p
    const [items, setItems] = useState<Assignation[]>([])
    const resolver = useRef<((insertionIndex: number) => void) | null>(null)
    const tempBest = useRef<any>(null)
    const comparisonNumber = useRef<number>(1)
    const params = commons.state.params
    const confirmationRequired = true
    const pickStm: SinglePicker = useCallback(async (type, assignations) => {
        info('pickStm', { type, assignations })
        assignations = _.uniqBy(assignations, (a) => a.id)
        // FIXME unicity issue
        // if (assignations.length === 1) {
        //     // DEMO
        //     return 0
        // }
        // commons.setState((s: any) => ({ ...s, currentStmIndex: step }))
        setItems([...assignations])
        setPickState(type)

        return new Promise<number>((rs, _) => {
            resolver.current = (insertionIndex: number) => {
                info(
                    'onPick resolver()',
                    commons.state.currentBucketRanking,
                    insertionIndex,
                    // choiceId,
                    type
                )
                comparisonNumber.current++

                commons.setState((s: any) => {
                    const newS = {
                        ...s,
                        steps: s.steps + 1,
                        currentBucketRanking: Math.min(
                            s.currentBucketRanking + 1,
                            MAX_BUCKET_RANKING
                        ),
                    }
                    // info('newState', {s, newS});
                    return newS
                })
                rs(insertionIndex)
            }
        })
    }, [])

    useEffect(() => {
        info('useEffect commons', commons.rounds.length)
        if (!commons.rounds.length) {
            info('useEffect commons.rounds empty')
            return
        }
        partialBucketFromRounds(commons.rounds as Assignation[][], pickStm)
            .then(commons.onRankBucketComplete)
            // FIXME error message for user! otherwise keeps increasing steps
            .catch((e) => logException('RankForBucket', 'useEffect', e))
    }, [commons.rounds.length, pickStm])

    const [pickState, setPickState] = useState<'BEST' | 'WORST'>('BEST')
    const [id2submit, setId2submit] = useState<string>('')

    const onPick = (targetId: string) => {
        // traceability
        commons.setSessionData(
            (p: SessionData) =>
                ({
                    ...p,
                    bucketRound: [
                        ...p.bucketRound,
                        {
                            comparedStms: items,
                            choiceId: targetId,
                            type: pickState,
                        },
                    ],
                }) as SessionData
        )
        // pick()
        const targetItemIndex = items.findIndex(({ id }) => targetId === id)
        if (confirmationRequired) {
            setId2submit(targetId)
            commons.setIndex2submit(targetItemIndex)
        } else {
            resolver.current && resolver.current(targetItemIndex)
            commons.setIndex2submit(-1)
        }
    }

    // Override title
    // useEffect(() => {
    //     if (!isMobile()) return
    //     commons.setGlobalState((s: GLOBAL_STATE_TYPE) => ({
    //         ...s,
    //         overrideTitle:
    //             pickState === 'BEST'
    //                 ? t('subtitle_rank_best')
    //                 : t('subtitle_rank_least'),
    //     }))
    // }, [pickState])

    // A. forced animation
    // useEffect(() => {
    //     if (!id2submit) return
    //     const targetItemIndex = items.findIndex(({ id }) => id2submit === id)
    //     info('useEffect id2submit', { id2submit, targetItemIndex })
    //     switch (pickState) {
    //         case 'BEST':
    //             moveUp(id2submit, targetItemIndex)
    //             break
    //         case 'WORST':
    //             moveDown(id2submit, items.length - 1 - targetItemIndex)
    //             break
    //     }
    // }, [id2submit, pickState, items])

    // B. manual dnd
    useEffect(() => {
        if (!items?.length) return
        switch (pickState) {
            case 'BEST':
                onPick(items[0].id)
                break
            case 'WORST':
                onPick(items[items.length - 1].id);
                break
        }
    }, [pickState, items])

  
    const t = useTranslation(commons)

    const stmProps = useCallback(
        (id: string, index: number, title: string) => ({
            // textSize: 'h6' as Variant, // FIXME repsonsive?
            fontType: 'small' as DFType,
            showRank: false,
            showId: commons.state.params.assessmentParams['debug_mode'] === 1,
            // selected: id2submit === id,
            // highlight: id2submit === id,
            draggable: true,
            emoji: !!commons.state.params.assessmentParams['values_with_icons'],
            disabled: tempBest?.current?.id === id,
            // style: {
            //     backgroundColor:
            //         tempBest?.current?.id === id ? 'grey' : 'white',
            // },
            index,
            id,
            title: tempBest?.current?.id === id ? `🔝 ${title}` : title,
            // onPickStm: async (d: any) => onPick(d),
        }),
        [id2submit]
    )



    return (
        <div
            className={twMerge(
                'p-2 flex flex-1 flex-col justify-center items-center w-full space-y-5',
                className
            )}
            {...rest}
        >
            <MainTitle title={t('subtitle_rank_reorder')} />
            {/*<Typography
                variant="body2"
                align="center"
                className="mb-auto hidden md:block"
            >
                {t('subtitle_rank_reorder')}
                 {pickState === 'BEST'
                    ? t('subtitle_rank_best')
                    : t('subtitle_rank_least')} 
            </Typography>*/}
            <ScaleHint 
                // fading={pickState === 'BEST'} 
                type={'BEST'} 
                />
            <DraggableAssignationList
                stmProps={stmProps}
                items={items}
                setItems={(as) => setItems(as)}
            />
            <ScaleHint 
                // fading={pickState === 'WORST'} 
                type={'WORST'} 
                />
            {confirmationRequired && (
                <Button
                    className="w-full mt-auto"
                    onClick={() => {
                        if (!id2submit) return
                        if (resolver.current) {
                            commons.setSessionData(
                                (p: SessionData) =>
                                    ({
                                        ...p,
                                        bucketRound: [
                                            ...p.bucketRound,
                                            {
                                                comparedStms: items,
                                                choiceId: id2submit,
                                                type: pickState,
                                            },
                                        ],
                                    }) as SessionData
                            )
                            resolver.current(commons.index2submit)
                            commons.setIndex2submit(-1)
                            setId2submit('')
                        }
                    }}
                    disabled={id2submit === ''}
                    variant="contained"
                    color="primary"
                >
                    {`${comparisonNumber.current}/${MAX_BUCKET_RANKING} - 
                        ${
                            comparisonNumber.current === MAX_BUCKET_RANKING
                                ? t('btn_finish')
                                : t('btn_next_str')
                        }`}
                </Button>
            )}
        </div>
    )
}
